import InputBank from '@components/inputs/input-bank/input-bank.component'
import { Bank, BorderWithIcon, Input, InputDigits, InputDocumentCpf, ObjectHelper, Selector, ValidatorHelper } from '@hub/shared'
import PaymentPayloadHelper from '@pages/payments-data/helper/payment-payload.helper'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './form-receive-bank.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  payload: Bank | null
  initialPayload: Bank | null
  canEdit: boolean
  hideData: boolean
  onPayloadChange?: (payload: Bank) => any
  onValidate?: (valid: boolean) => any
}

export default ({ payload, initialPayload, canEdit, hideData, onPayloadChange, onValidate }: Props) => {
  const validatorHelper = useMemo(() => new ValidatorHelper(), [])
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const paymentPayloadHelper = useMemo(() => new PaymentPayloadHelper(), [])
  const [t] = useTranslation('paymentFormReceiveBank')
  const [valid, setValid] = useState<boolean>(true)
  const [state, setState] = useState<Bank | null>(null)

  useEffect(() => {
    if (!objectHelper.compareObjects(state, payload)) {
      setState({ ...payload! })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload])

  const payloadChange = useCallback(
    (value: Bank) => {
      setState({ ...value })

      if (onPayloadChange && typeof onPayloadChange === 'function') {
        onPayloadChange(value)
      }
    },
    [onPayloadChange]
  )

  const hideDataCheck = useCallback(
    (data?: string) => {
      if (hideData) {
        return (
          <>
            {Array.from(Array(data?.length).keys()).map(() => {
              return '•'
            })}
          </>
        )
      }
      return data
    },
    [hideData]
  )

  useEffect(() => {
    if (state?.paymentType === 'nf') {
      if (state?.isPersonAccount === 0 && (state?.holderDocument !== state?.companyDocument || state?.holderName !== state?.companyName)) {
        state.holderDocument = state?.companyDocument
        state.holderName = state?.companyName
        payloadChange(state)
      }
      if (!valid && state?.isPersonAccount === 0) {
        setValid(true)
      }
    }
  }, [state, initialPayload, payloadChange, valid])

  const handleChange = useCallback(
    (fieldName: string, fieldValue: any) => {
      if (fieldName === 'isPersonAccount' && fieldValue === 1) {
        state!.holderDocument = ''
        state!.holderName = ''
        setValid(false)
      }
      if (fieldName === 'bankCode' && fieldValue?.includes(',')) {
        const [bankCode, bankName] = fieldValue?.split(',')
        state!.bankCode = bankCode
        state!.bankName = bankName
      } else if (fieldName !== 'bankCode') {
        ;(state as any)[fieldName] = fieldValue
      }

      payloadChange(state!)
    },
    [state, payloadChange]
  )

  useEffect(() => {
    if (onValidate && typeof onValidate === 'function') {
      onValidate(paymentPayloadHelper.isFormReceiveBankValid(state!, valid))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, state, paymentPayloadHelper])

  let selectAccountType = null
  if (state?.typeOfBusiness === 'MEI (Microempreendedor Individual)') {
    selectAccountType = (
      <div>
        <p className="mb-4" style={{ fontWeight: 500 }}>
          {t('accountType')}
        </p>
        <Selector
          name={`radio-account-holder-type${state?.isPersonAccount === 1 ? '-checked' : ''}`}
          id={`radio-account-holder-type${state?.isPersonAccount === 1 ? '-checked' : ''}`}
          rightLabel={t('cpfAccount')}
          value={state?.isPersonAccount}
          checked={state?.isPersonAccount === 1}
          onChange={(checked: boolean) => {
            if (checked) {
              handleChange('isPersonAccount', 1)
            }
          }}
          type="radio"
          className="mr-5"
          readOnly={hideData || !canEdit}
        />
        <Selector
          name={`radio-account-holder-type${state?.isPersonAccount === 0 ? '-checked' : ''}`}
          id={`radio-account-holder-type${state?.isPersonAccount === 0 ? '-checked' : ''}`}
          rightLabel={t('cnpjAccount')}
          value={state?.isPersonAccount}
          checked={state?.isPersonAccount === 0}
          onChange={(checked: boolean) => {
            if (checked) {
              handleChange('isPersonAccount', 0)
            }
          }}
          type="radio"
          readOnly={hideData || !canEdit}
        />
      </div>
    )
  }

  let holderDocument = (
    <div className="holder-cpf mb-4">
      <i className="fa-solid fa-id-card mr-3" style={{ fontSize: '32px', color: 'var(--text_color)' }}></i>
      <div>
        <p>
          <b>
            {state?.holderDocument
              ? hideDataCheck(validatorHelper?.[state?.holderDocument?.length === 11 ? 'setCpfMask' : 'setCnpjMask'](state?.holderDocument))
              : '-'}
          </b>
        </p>
        <p>{state?.holderDocument?.length === 11 ? t('holderCpf') : t('holderCnpj')}</p>
      </div>
    </div>
  )
  if (state?.typeOfBusiness === 'MEI (Microempreendedor Individual)' && state?.isPersonAccount === 1) {
    holderDocument = (
      <InputDocumentCpf
        className="mb-2"
        name="cpf-personal-account"
        id="cpf-personal-account"
        label={t('holderCpf')}
        value={state?.holderDocument}
        onChange={(value) => handleChange('holderDocument', value)}
        onValidate={(valid) => setValid(valid)}
        disabled={false}
        type={hideData ? 'password' : 'text'}
        readOnly={hideData || !canEdit}
        canRepeat
      />
    )
  }

  let holderName = (
    <div className="holder-cpf mb-4">
      <i className="fa-solid fa-id-card mr-3" style={{ fontSize: '32px', color: 'var(--text_color)' }}></i>
      <div>
        <p>
          <b>{state?.holderName ? hideDataCheck(state?.holderName) : '-'}</b>
        </p>
        <p>{t('holderCompanyName')}</p>
      </div>
    </div>
  )
  if (state?.paymentType === 'rpa' || (state?.typeOfBusiness === 'MEI (Microempreendedor Individual)' && state?.isPersonAccount === 1)) {
    holderName = (
      <Input
        name="holderName-account"
        id="holderName-account"
        label={t('holderName')}
        value={state?.holderName}
        onChange={(value) => handleChange('holderName', value)}
        type={hideData ? 'password' : 'text'}
        required
        requiredOnLoad
        readOnly={hideData || !canEdit}
      />
    )
  }

  return (
    <div className={'form-receive-bank mt-7'}>
      <h5 className="mb-5">{t('title')}</h5>
      <p className="mb-4" style={{ fontWeight: 500 }}>
        {t('bankAccountType')}
      </p>
      <div className="display-flex">
        <Selector
          name={`radio-account-bank-type${state?.bankAccountType === 'checking' ? '-checked' : ''}`}
          id={`radio-account-bank-type${state?.bankAccountType === 'checking' ? '-checked' : ''}`}
          rightLabel={t('checkingAccount')}
          value={state?.bankAccountType}
          checked={state?.bankAccountType === 'checking'}
          onChange={(checked: boolean) => {
            if (checked) {
              handleChange('bankAccountType', 'checking')
            }
          }}
          type="radio"
          className="mr-5"
          readOnly={hideData || !canEdit}
        />
        <Selector
          name={`radio-account-bank-type${state?.bankAccountType === 'savings' ? '-checked' : ''}`}
          id={`radio-account-bank-type${state?.bankAccountType === 'saving' ? '-checked' : ''}`}
          rightLabel={t('savingsAccount')}
          value={state?.bankAccountType}
          checked={state?.bankAccountType === 'savings'}
          onChange={(checked: boolean) => {
            if (checked) {
              handleChange('bankAccountType', 'savings')
            }
          }}
          type="radio"
          readOnly={hideData || !canEdit}
        />
      </div>
      <InputBank
        name="bank-select"
        id="bank-select"
        label={t('bank')}
        value={state?.bankCode || ''}
        onChange={(value: any) => handleChange('bankCode', value)}
        disabled={false}
        className="mb-3"
        readOnly={hideData || !canEdit}
      />
      <InputDigits
        name="input-agency"
        id="input-agency"
        label={t('agency')}
        value={state?.bankAccountAgency}
        onChange={(value) => handleChange('bankAccountAgency', value)}
        type={hideData ? 'password' : 'text'}
        maxLength={4}
        required
        requiredOnLoad
        className="mb-3"
        readOnly={hideData || !canEdit}
      />
      <div className="display-flex mb-5">
        <InputDigits
          name="input-account-number"
          id="input-account-number"
          label={t('account')}
          value={state?.bankAccountNumber}
          onChange={(value) => handleChange('bankAccountNumber', value)}
          type={hideData ? 'password' : 'text'}
          maxLength={10}
          required
          requiredOnLoad
          className="mr-3"
          readOnly={hideData || !canEdit}
        />
        <Input
          name="input-account-digit"
          id="input-account-digit"
          label={t('digit')}
          value={state?.bankAccountDigit}
          onChange={(value) => handleChange('bankAccountDigit', value)}
          maxLength={1}
          type={hideData ? 'password' : 'text'}
          noCounting
          readOnly={hideData || !canEdit}
        />
      </div>
      <BorderWithIcon className="pb-3">
        <p className="mb-2">
          <b>{t('remember')}</b>
        </p>
        <p className="mb-4" dangerouslySetInnerHTML={{ __html: t('warning') }} />
        {selectAccountType}
        {holderDocument}
        {holderName}
      </BorderWithIcon>
    </div>
  )
}
